import React, { useEffect, useRef, useState } from "react";
import {
  ACTIVITY_STATUS,
  AUDIT_STATUS,
  STATUS_MAPPING,
} from "../../common/Constants";
import { validateFileWithContent, FILE_TYPE_GROUPS, FILE_SIZE_LIMITS, generateAcceptAttribute, VALIDATION_MESSAGES } from "../../../utils/fileValidation";
import { useGetVendorActivityDocuments } from "../../../backend/query";
import { checkAuditorActivityStatus, extractTextContent, preventDefault, downloadWithPresignedUrl } from "../../../utils/common";
import "./ActivityModalV2.css";
import * as api from "../../../backend/request";
import { toast } from "react-toastify";
import Select from "react-select";
import DatePicker from "react-datepicker";
import PageLoader from "../../shared/PageLoader";
import { ACTIVITY_TYPE, EDITOR_CONFIG } from "../../../utils/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { AxiosError } from "axios";
import ConfirmModal from "../../common/ConfirmModal";
import ReactQuill, { Quill } from "react-quill";
import cloudUpload from "../../../assets/img/icon/cloudUpload.svg";

// Ensure links without a protocol open correctly instead of being treated as relative paths
const QuillLink = Quill.import('formats/link') as any;
QuillLink.sanitize = function(url: string) {
  if (url && !/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)) {
    return 'https://' + url;
  }
  return url;
};
Quill.register(QuillLink, true);

const FORM_STATUSES = [
  { value: ACTIVITY_STATUS.APPROVE, label: "Approve" },
  { value: ACTIVITY_STATUS.REJECT, label: "Reject" },
];

const AUDIT_STATUSES = [
  { value: AUDIT_STATUS.COMPLIANT, label: STATUS_MAPPING[AUDIT_STATUS.COMPLIANT] },
  { value: AUDIT_STATUS.NON_COMPLIANCE, label: STATUS_MAPPING[AUDIT_STATUS.NON_COMPLIANCE] },
  { value: AUDIT_STATUS.NOT_APPLICABLE, label: STATUS_MAPPING[AUDIT_STATUS.NOT_APPLICABLE] },
];

function StatusTmp({ status }: any) {
  return <span className={`status-${status}`}>{STATUS_MAPPING[status]}</span>;
}

function AuditorVendorActivityModal({ activity = {}, auditStatus: vendorAuditStatus, onClose, onSubmit }: any) {
  const [formStatus, setFormStatus] = useState(checkAuditorActivityStatus(activity, 'todo_vendor', vendorAuditStatus));
  const [submitting, setSubmitting] = useState(false);
  const [auditStatus, setAuditStatus] = useState<any>(
    AUDIT_STATUSES.find((x: any) => x.value === activity.audit_status) || null
  );
  const [status, setStatus] = useState<any>(
    FORM_STATUSES.find((x: any) => x.value === activity.status)
  );
  const [auditRemarks, setAuditRemarks] = useState(activity.audit_remarks);
  const [formsStatusRemarks, setFormsStatusRemarks] = useState(activity.forms_status_remarks);
  const [dueDate, setDueDate] = useState<any>(new Date(activity.due_date));
  const { documents, invalidate } = useGetVendorActivityDocuments(activity.id);
  const [file, setFile] = useState<any>(null);
  const [invalidFile, setInvalidFile] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [enableEditAlert, setEnableEditAlert] = useState<boolean>(false);
  const [reasonForChange, setReasonForChange] = useState<any>();
  const [editing, setEditing] = useState<boolean>(false);
  const editorConfig = EDITOR_CONFIG;
  const Block = Quill.import('blots/block');
  Block.tagName = 'DIV';
  Quill.register(Block, true);

  function isInvalid() {
    const safeFormsStatusRemarks = extractTextContent(formsStatusRemarks || "").trim();
    const safeAuditRemarks = extractTextContent(auditRemarks || "").trim();
    return (
      !status ||
      (status.value === ACTIVITY_STATUS.APPROVE && !auditStatus) ||
      (activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT &&
        status.value === ACTIVITY_STATUS.REJECT &&
        !auditStatus) ||
      (activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT &&
        status.value === ACTIVITY_STATUS.REJECT &&
        !safeFormsStatusRemarks) ||
      ([AUDIT_STATUS.NON_COMPLIANCE].includes((auditStatus || {}).value) && !safeAuditRemarks) ||
      ([ACTIVITY_STATUS.APPROVE].includes(status.value) &&
        [AUDIT_STATUS.NOT_APPLICABLE].includes((auditStatus || {}).value) &&
        safeFormsStatusRemarks.length < 10)
    );
  }

  function getValidationErrors(): string[] {
    const safeFormsStatusRemarks = extractTextContent(formsStatusRemarks || "").trim();
    const safeAuditRemarks = extractTextContent(auditRemarks || "").trim();
    const errors: string[] = [];

    if (!status) {
      errors.push("Please select an Evidence Status.");
      return errors;
    }

    if (status.value === ACTIVITY_STATUS.APPROVE && !auditStatus) {
      errors.push("Please select a Compliance Status.");
    }

    if (activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT && status.value === ACTIVITY_STATUS.REJECT) {
      if (!auditStatus) errors.push("Please select a Compliance Status.");
      else if (!safeFormsStatusRemarks) errors.push("Observations are required.");
    }

    if ([AUDIT_STATUS.NON_COMPLIANCE].includes((auditStatus || {}).value) && !safeAuditRemarks) {
      errors.push("Recommendations are required for Non-Compliance.");
    }

    if (
      status.value === ACTIVITY_STATUS.APPROVE &&
      (auditStatus || {}).value === AUDIT_STATUS.NOT_APPLICABLE &&
      safeFormsStatusRemarks.length < 10
    ) {
      errors.push("Observations must be at least 10 characters long.");
    }

    if (activity.published && (!reasonForChange || reasonForChange.length === 0)) {
      errors.push("Reason for change is required.");
    }

    return errors;
  }

  function submit() {
    const errors = getValidationErrors();
    if (errors.length > 0) {
      errors.forEach((msg) => toast.error(msg));
      return;
    }

    setSubmitting(true);
    const payload: any = activity.published
      ? {
          audit_remarks: auditRemarks || "",
          audit_status: (auditStatus || {}).value || "",
          forms_status_remarks: formsStatusRemarks || "",
          status: status.value,
        }
      : {
          todo_vendor: {
            audit_remarks: auditRemarks || "",
            audit_status: (auditStatus || {}).value || "",
            forms_status_remarks: formsStatusRemarks || "",
            status: status.value,
            audited_date: new Date().toISOString(),
          },
        };

    if ([ACTIVITY_STATUS.REJECT].includes(status.value)) {
      if (activity.published) {
        payload.due_date = new Date(dueDate).toISOString();
      } else {
        payload.todo_vendor.due_date = new Date(dueDate).toISOString();
      }
    }
    if (activity.published) {
      payload.todo_vendor_id = activity.id;
      payload.reason_for_change = reasonForChange;
    }

    if (activity.published) {
      api
        .post("/api/v1/todo_vendors/edit_post_publish", payload)
        .then((response: any) => {
          if (response instanceof AxiosError) {
            const data = (response.response || {}).data;
            toast.error((data || {}).error || "Activity updated failed. Please try again");
          } else {
            toast.success("Activity updated successfully.");
            onSubmit();
            onClose();
          }
        })
        .finally(() => setSubmitting(false));
    } else {
      const action = status.value === 'Approved' ? 'approve' : 'reject';
      api
        .put(`/api/v1/todo_vendors/${activity.id}/${action}`, payload)
        .then((response: any) => {
          if (response instanceof AxiosError) {
            const data = (response.response || {}).data;
            toast.error((data || {}).error || "Activity updated failed. Please try again");
          } else {
            toast.success("Activity updated successfully.");
            onSubmit();
            onClose();
          }
        })
        .finally(() => setSubmitting(false));
    }
  }

  async function onFileChange(event: any) {
    const selectedFile = event.target.files[0];
    if (!selectedFile) { setFile(null); setInvalidFile(false); return; }
    setInvalidFile(false);
    const validation = await validateFileWithContent(selectedFile, FILE_TYPE_GROUPS.ALL_DOCUMENTS, FILE_SIZE_LIMITS.LARGE);
    if (!validation.isValid) {
      setInvalidFile(true); setFile(null);
      toast.error(validation.error);
      event.target.value = '';
      return;
    }
    setFile(selectedFile);
    setInvalidFile(false);
  }

  function clearFile() {
    setFile(null);
    setInvalidFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function uploadFile() {
    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", file, file.name);
    api
      .post(`/api/v1/todo_vendor_details/upload?todo_vendor_id=${activity.id}`, formData)
      .then(() => {
        setFile(null);
        setInvalidFile(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        invalidate();
        toast.success("File uploaded successfully.");
      })
      .finally(() => setSubmitting(false));
  }

  function onStatusChange(event: any) {
    setStatus(event);
    if (event && event.value) { setDueDate(new Date(activity.due_date)); setAuditStatus(null); }
  }

  useEffect(() => { if (!dueDate) setDueDate(new Date(activity.due_date)); }, [dueDate]);

  function handleEdit(e: any) {
    e.preventDefault();
    if (activity.auditted === ACTIVITY_TYPE.NO_AUDIT) {
      setFormStatus({ editable: false, type: 'info', message: `This activity doesn't required any action.`, alertMessage: "" });
    } else {
      setFormStatus({ editable: true });
    }
    setEditing(true);
  }

  return (
    <>
      <div className="la-drawer-backdrop open" />
      <div className="la-drawer activity-edit-drawer open">
        <div className="la-drawer-head">
          <div className="la-drawer-title">Activity</div>
          <button className="la-drawer-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="ae-body">
          <div className="ae-section">

            {(formStatus || {}).message && (
              <div className="ae-row" style={{ display: "block", padding: "10px 0" }}>
                <div className={`alert alert-${(formStatus as any).type}`} style={{ margin: 0 }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{(formStatus as any).message}</span>
                    {activity.auditted === ACTIVITY_TYPE.AUDIT &&
                      activity.status === ACTIVITY_STATUS.AUDITED &&
                      (!activity.published || editing) && (
                        <span style={{ cursor: "pointer" }} onClick={() => setEnableEditAlert(true)}>
                          <FontAwesomeIcon icon={faPencil} />
                          <span className="px-1">Edit</span>
                        </span>
                      )}
                  </div>
                </div>
              </div>
            )}

            <div className="ae-row">
              <label>Act</label>
              <div className="ae-val">
                <div>{(activity.act || {}).name || '—'}</div>
                {(activity.act || {}).establishment_type && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{(activity.act || {}).establishment_type}</div>
                )}
              </div>
            </div>
            <div className="ae-row">
              <label>Rule</label>
              <div className="ae-val">
                <div>{(activity.rule || {}).name || '—'}</div>
                {((activity.rule || {}).unique_identifier || (activity.rule || {}).section_no || (activity.rule || {}).rule_no) && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(activity.rule || {}).unique_identifier && (
                      <span><span style={{ fontWeight: 600 }}>ID:</span> {(activity.rule || {}).unique_identifier}</span>
                    )}
                    {(activity.rule || {}).section_no && (
                      <span><span style={{ fontWeight: 600 }}>Section No:</span> {(activity.rule || {}).section_no}</span>
                    )}
                    {(activity.rule || {}).rule_no && (
                      <span><span style={{ fontWeight: 600 }}>Rule No:</span> {(activity.rule || {}).rule_no}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="ae-row">
              <label>Forms/Registers &amp; Returns</label>
              <div className="ae-val">
                <div>{(activity.activity || {}).name || '—'}</div>
                {(activity.activity || {}).activity_type && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{(activity.activity || {}).activity_type}</div>
                )}
              </div>
            </div>
            <div className="ae-row">
              <label>Activity Type</label>
              <div className="ae-val">{(activity.activity || {}).activity_type || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Month (Year)</label>
              <div className="ae-val">{activity.month ? `${activity.month} (${activity.year})` : '—'}</div>
            </div>
            <div className="ae-row">
              <label>Evidence Status</label>
              <div className="ae-val">{activity.status ? <StatusTmp status={activity.status} /> : '—'}</div>
            </div>
            <div className="ae-row">
              <label>Audit Type</label>
              <div className="ae-val">{activity.auditted || ACTIVITY_TYPE.AUDIT}</div>
            </div>
            <div className="ae-row">
              <label>Auditee Remarks</label>
              <div className="ae-val">{activity.auditee_remarks || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Compliance Status</label>
              <div className="ae-val">{activity.audit_status || '—'}</div>
            </div>
            <div className="ae-row">
              <label>Observations</label>
              <div className="ae-val"><div dangerouslySetInnerHTML={{ __html: activity.forms_status_remarks || "" }} /></div>
            </div>
            <div className="ae-row">
              <label>Recommendations</label>
              <div className="ae-val"><div dangerouslySetInnerHTML={{ __html: activity.audit_remarks || "" }} /></div>
            </div>

            {(formStatus || {}).editable && (
              <>
                <div className="ae-row">
                  <label>Evidence Status<span className="required">*</span></label>
                  <Select
                    classNamePrefix="select"
                    placeholder="Evidence Status"
                    options={FORM_STATUSES}
                    onChange={onStatusChange}
                    value={status}
                  />
                </div>

                {(status || {}).value === ACTIVITY_STATUS.APPROVE && (
                  <div className="ae-row">
                    <label>Compliance Status<span className="required">*</span></label>
                    <Select
                      classNamePrefix="select"
                      placeholder="Compliance Status"
                      options={AUDIT_STATUSES.filter((o) => [AUDIT_STATUS.COMPLIANT, AUDIT_STATUS.NOT_APPLICABLE].includes(o.value))}
                      onChange={setAuditStatus}
                      value={auditStatus}
                    />
                  </div>
                )}

                {(status || {}).value === ACTIVITY_STATUS.REJECT && activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT && (
                  <div className="ae-row">
                    <label>Compliance Status<span className="required">*</span></label>
                    <Select
                      classNamePrefix="select"
                      placeholder="Compliance Status"
                      options={AUDIT_STATUSES.filter((o) => [AUDIT_STATUS.NON_COMPLIANCE, AUDIT_STATUS.NOT_APPLICABLE].includes(o.value))}
                      onChange={setAuditStatus}
                      value={auditStatus}
                    />
                  </div>
                )}

                <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                  <label>
                    Observations
                    {auditStatus && [AUDIT_STATUS.NON_COMPLIANCE, AUDIT_STATUS.NOT_APPLICABLE, ACTIVITY_STATUS.REJECT].includes(auditStatus.value) && (
                      <span className="required">*</span>
                    )}
                  </label>
                  <ReactQuill
                    theme="snow"
                    className={`form-control ${auditStatus && (status || {}).value === ACTIVITY_STATUS.APPROVE && auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE && extractTextContent(formsStatusRemarks || "").length < 10 ? 'is-invalid' : ''}`}
                    modules={editorConfig.modules}
                    formats={editorConfig.formats}
                    value={formsStatusRemarks}
                    onChange={setFormsStatusRemarks}
                    id="quillContainer_formsStatusRemarks"
                    bounds=".ae-body"
                  />
                  {auditStatus && (status || {}).value === ACTIVITY_STATUS.APPROVE && auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE && (
                    <div className={`mt-1 ${extractTextContent(formsStatusRemarks || "").length < 10 ? "text-danger" : "text-primary"}`}>
                      {extractTextContent(formsStatusRemarks || "").length < 10 ? "At least 10 characters required" : ""}
                    </div>
                  )}
                </div>

                {(status || {}).value === ACTIVITY_STATUS.APPROVE && (
                  <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                    <label>Recommendations</label>
                    <ReactQuill
                      theme="snow"
                      className="form-control"
                      modules={editorConfig.modules}
                      formats={editorConfig.formats}
                      value={auditRemarks}
                      onChange={setAuditRemarks}
                      id="quillContainer_auditRemarks"
                      bounds=".ae-body"
                    />
                  </div>
                )}

                {(status || {}).value === ACTIVITY_STATUS.REJECT && activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT && (
                  <>
                    <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                      <label>
                        Recommendations
                        {[AUDIT_STATUS.NON_COMPLIANCE].includes((auditStatus || {}).value) && <span className="required">*</span>}
                      </label>
                      <ReactQuill
                        theme="snow"
                        className="form-control"
                        modules={editorConfig.modules}
                        formats={editorConfig.formats}
                        value={auditRemarks}
                        onChange={setAuditRemarks}
                        id="quillContainer_auditRemarks_reject"
                        bounds=".ae-body"
                      />
                    </div>
                    <div className="ae-row">
                      <label>
                        Vendor Due Date
                        {[AUDIT_STATUS.NON_COMPLIANCE].includes((auditStatus || {}).value) && <span className="required">*</span>}
                      </label>
                      <DatePicker
                        className="ae-date"
                        selected={dueDate}
                        dateFormat="dd-MM-yyyy"
                        onChange={setDueDate}
                        placeholderText="dd-mm-yyyy"
                        minDate={new Date()}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </div>
                  </>
                )}

                {activity.published && (
                  <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                    <label>Reason for Change<span className="required">*</span></label>
                    <textarea
                      className="ae-textarea"
                      value={reasonForChange}
                      onChange={(e) => setReasonForChange(e.target.value)}
                    />
                  </div>
                )}
              </>
            )}

            <div className="ae-evidence-header">Evidence</div>

            {/* File upload — only for editable physical audit */}
            {(formStatus || {}).editable && activity.auditted === ACTIVITY_TYPE.PHYSICAL_AUDIT && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={onFileChange}
                  accept={generateAcceptAttribute(FILE_TYPE_GROUPS.ALL_DOCUMENTS)}
                />
                {!file ? (
                  <div className="ae-upload-box" onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}>
                    <div className="mb-2">
                      <img src={cloudUpload} alt="upload" />
                    </div>
                    <p className="mb-2">Click to browse and upload</p>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); }}
                    >
                      Choose File
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="ae-upload-success">
                      <span className="ae-upload-filename">{file.name}</span>
                      <button className="ae-upload-remove" onClick={clearFile} type="button">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z" stroke="#757575" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <button
                      className="ae-upload-btn"
                      disabled={invalidFile || submitting}
                      onClick={uploadFile}
                      type="button"
                    >
                      {submitting ? 'Uploading...' : 'Upload'}
                    </button>
                  </>
                )}
                {invalidFile && (
                  <div className="text-danger mt-1">
                    <small>{VALIDATION_MESSAGES.INVALID_FILE_ALL_DOCS}</small>
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: 16 }}>
              <table className="table modalTable fixed_header">
                <thead>
                  <tr>
                    <th style={{ width: "75%" }}>File Name</th>
                    <th style={{ width: "25%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ height: "300px" }}>
                  {documents.map((doc: any, index: number) => (
                    <tr key={index}>
                      <td width="75%" style={{ overflowWrap: "break-word" }}>{doc.file_name}</td>
                      <td width="25%">
                        <div className="d-flex flex-row align-items-center">
                          <span style={{ zoom: 1.6, opacity: 0.5, cursor: "pointer" }} onClick={() => downloadWithPresignedUrl(doc.file_name, doc.file_path)} title="Download">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9.75 7.875V9.75H2.25V7.875H1V9.75C1 10.4375 1.5625 11 2.25 11H9.75C10.4375 11 11 10.4375 11 9.75V7.875H9.75Z" fill="var(--bs-blue)" />
                              <path d="M9.125 5.375L8.24375 4.49375L6.625 6.10625L6.625 1L5.375 1L5.375 6.10625L3.75625 4.49375L2.875 5.375L6 8.5L9.125 5.375Z" fill="var(--bs-blue)" />
                            </svg>
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {documents.length === 0 && (
                    <tr>
                      <td colSpan={2} rowSpan={4} width="100%" style={{ height: "200px", border: 0 }}>
                        <div className="d-flex w-100 h-100 align-items-center justify-content-center">No files available</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        <div className="ae-foot">
          {(formStatus || {}).editable ? (
            <>
              <button className="ae-back" onClick={onClose}>Cancel</button>
              <button
                className="ae-submit"
                onClick={submit}
                disabled={submitting}
                style={isInvalid() ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </>
          ) : (
            <button className="ae-submit" style={{ flex: 1 }} onClick={onClose}>Close</button>
          )}
        </div>
      </div>

      {enableEditAlert && (
        <ConfirmModal
          message={(formStatus as any).alertMessage}
          onClose={(e: any) => { preventDefault(e); setEnableEditAlert(false); }}
          onSubmit={handleEdit}
        />
      )}
      {submitting && <PageLoader />}
    </>
  );
}

export default AuditorVendorActivityModal;
